import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useMemo } from 'react';

export interface UseLoginResult {
  /**
   * Function to initiate Auth0 login with redirect
   */
  handleLogin: () => void;
  /**
   * Whether the user was just logged out
   */
  isLoggedOut: boolean;
  /**
   * Whether the session expired (401 error)
   */
  isSessionExpired: boolean;
  /**
   * Whether an authentication error occurred (e.g., invalid user)
   */
  isAuthError: boolean;
  /**
   * Whether the user is currently authenticated
   */
  isAuthenticated: boolean;
  /**
   * Whether authentication state is loading
   */
  isLoading: boolean;
}

/**
 * Hook for handling login page logic
 *
 * @example
 * ```tsx
 * const MyLoginPage = () => {
 *   const { handleLogin, isLoggedOut } = useLogin();
 *
 *   return (
 *     <div>
 *       <h1>Welcome</h1>
 *       {isLoggedOut && <p>You have been logged out</p>}
 *       <button onClick={handleLogin}>Login</button>
 *     </div>
 *   );
 * };
 * ```
 */
export function useLogin(): UseLoginResult {
  const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();

  // Check if user was logged out or session expired (from query params)
  const searchParams = useMemo(() => {
    if (typeof window === 'undefined') return new URLSearchParams();
    return new URLSearchParams(window.location.search);
  }, []);

  const isLoggedOut = searchParams.get('loggedOut') === 'true';
  const isSessionExpired = searchParams.get('sessionExpired') === 'true';
  const isAuthError = searchParams.get('error') === 'auth';

  // Clean up the URL after reading the query param (preserve returnTo)
  useEffect(() => {
    const hasLoggedOut = searchParams.get('loggedOut') === 'true';
    const hasSessionExpired = searchParams.get('sessionExpired') === 'true';
    const hasError = searchParams.get('error');
    const returnTo = searchParams.get('returnTo');

    if ((hasLoggedOut || hasSessionExpired || hasError) && typeof window !== 'undefined') {
      // Preserve returnTo param if present, remove loggedOut, sessionExpired, and error params
      const newUrl = returnTo ? `/login?returnTo=${encodeURIComponent(returnTo)}` : '/login';
      window.history.replaceState(
        {
          loggedOut: hasLoggedOut,
          sessionExpired: hasSessionExpired,
          authError: hasError === 'auth',
        },
        '',
        newUrl
      );
    }
  }, [searchParams]);

  const handleLogin = () => {
    // Get returnTo from URL query param or default to root
    const urlParams = new URLSearchParams(window.location.search);
    const returnTo = urlParams.get('returnTo') || '/';

    loginWithRedirect({
      appState: {
        returnTo,
      },
    });
  };

  return {
    handleLogin,
    isLoggedOut: isLoggedOut || false,
    isSessionExpired: isSessionExpired || false,
    isAuthError: isAuthError || false,
    isAuthenticated,
    isLoading,
  };
}
